<script setup>
import { computed } from 'vue';
import { Head, usePage, Link } from '@inertiajs/vue3';
import { ArrowLongRightIcon, DevicePhoneMobileIcon, EnvelopeIcon, LinkIcon, CalendarDaysIcon } from '@heroicons/vue/24/outline';
import { UserIcon } from '@heroicons/vue/24/solid';
import { useTitle as title } from '@/Composables/title.js';
import Breadcrumb from '@/Components/Breadcrumb.vue';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';

const props = defineProps({
    contributorProfile: {
        type: Object,
        required: false,
        default() {},
    },
});

const page = usePage();
const longLocale = computed(() => page.props.longLocale);
const locale = computed(() => page.props.locale);
const currentUrl = computed(() => page.props.currentUrl);
const appName = computed(() => page.props.appName);
const shortSocialMedia = computed(() => props.contributorProfile.social_media?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')?.split('/')[0] ?? props.contributorProfile.social_media);
const pageTitle = computed(() => title(`${props.contributorProfile.display_name} - ${props.contributorProfile?.company}`));
const hasContactInfo = computed(() => props.contributorProfile.phone || props.contributorProfile.email || props.contributorProfile.social_media);
</script>

<template>
    <Head>
        <title>{{ pageTitle }}</title>
        <meta
            name="robots"
            content="noindex,follow"
        >
        <meta
            name="description"
            content=""
        >
        <meta
            property="og:locale"
            :content="longLocale"
        >
        <meta
            property="og:url"
            :content="currentUrl"
        >
        <meta
            property="og:site_name"
            :content="appName"
        >
        <meta
            property="og:type"
            content="website"
        >
        <meta
            property="og:title"
            :content="pageTitle"
        >
        <meta
            property="og:description"
            content=""
        >
        <meta
            property="og:image"
            content=""
        >
    </Head>

    <div class="relative bg-white">
        <div class="container px-4 md:py-4 mx-auto">
            <Breadcrumb class="mt-4" />
        </div>

        <div class="max-w-4xl px-4 pt-8 pb-16 mx-auto text-gray-800 space-y-16">
            <div>
                <div class="grid grid-cols-3 md:grid-cols-8 rounded-xl overflow-hidden">
                    <div
                        class="col-span-1 md:col-span-3 bg-gray-50 md:bg-deep-teal-900"
                        :class="[{ 'flex items-center justify-center': !contributorProfile.image }]"
                    >
                        <picture
                            v-if="contributorProfile.image?.glide_avatar_big_image_url_avif"
                            class="shrink-0"
                        >
                            <source
                                :srcset="contributorProfile.image.glide_avatar_big_image_url_avif"
                                type="image/avif"
                            >
                            <source
                                :srcset="contributorProfile.image.glide_avatar_big_image_url_webp"
                                type="image/webp"
                            >
                            <img
                                class="w-full h-auto aspect-square object-cover rounded-xl md:rounded-none md:rounded-tl-xl"
                                :src="contributorProfile.image.glide_avatar_big_image_url_jpg"
                                :alt="`${$t('eventDetails.contributor.image.alt')} ${contributorProfile.display_name}`"
                                loading="lazy"
                            >
                        </picture>

                        <div
                            v-else
                            class="flex items-center justify-center shrink-0 size-16 md:size-24 bg-white rounded-full border"
                            aria-hidden="true"
                        >
                            <UserIcon class="size-8 md:size-14 text-gray-600" />
                        </div>
                    </div>

                    <div class="col-span-2 md:col-span-5 bg-gray-50 p-4 md:p-8">
                        <div class="flex flex-col h-full justify-center space-y-1.5">
                            <h1 class="font-semibold text-base leading-tight sm:text-2xl md:text-4xl lg:text-5xl md:space-y-0.5">
                                {{ contributorProfile.display_name }}
                            </h1>

                            <div class="text-sm sm:text-base md:text-lg text-gray-500">
                                <div v-if="contributorProfile.role">
                                    {{ contributorProfile.role }}
                                </div>

                                <div v-if="contributorProfile.company">
                                    {{ contributorProfile.company }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="col-span-3 order-last md:order-none bg-deep-teal-900 text-white p-8"
                        :class="[{ 'hidden md:block': !hasContactInfo }]"
                    >
                        <template v-if="hasContactInfo">
                            <div class="space-y-2.5">
                                <h2 class="font-semibold">
                                    {{ $t('contributorProfiles.show.contact') }}
                                </h2>

                                <div class="text-lg/tight space-y-3">
                                    <a
                                        v-if="contributorProfile.phone"
                                        :href="`tel:${contributorProfile.phone.e164}`"
                                        class="flex"
                                    >
                                        <DevicePhoneMobileIcon
                                            class="size-6 -mt-px mr-2 shrink-0"
                                            aria-hidden="true"
                                        />

                                        <span class="break-all">
                                            {{ contributorProfile.phone.display }}
                                        </span>
                                    </a>

                                    <a
                                        v-if="contributorProfile.email"
                                        :href="`mailto:${contributorProfile.email}`"
                                        class="flex"
                                    >
                                        <EnvelopeIcon
                                            class="size-6 -mt-px mr-2 shrink-0"
                                            aria-hidden="true"
                                        />

                                        <span class="break-all">
                                            {{ contributorProfile.email }}
                                        </span>
                                    </a>

                                    <div
                                        v-if="contributorProfile.social_media"
                                        class="flex"
                                    >
                                        <LinkIcon
                                            class="size-6 -mt-0.5 mr-2 shrink-0"
                                            aria-hidden="true"
                                        />

                                        <a
                                            :href="contributorProfile.social_media"
                                            class="break-all hover:underline"
                                            target="_blank"
                                            rel="nofollow noopener"
                                        >
                                            {{ shortSocialMedia }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="col-span-3 md:col-span-5 bg-gray-50 p-8 space-y-1">
                        <div v-html="contributorProfile.vita" />
                    </div>
                </div>

                <div
                    v-if="contributorProfile?.image?.creditLine"
                    class="text-sm text-gray-500 md:px-4 py-2"
                >
                    {{ $t('components.creditLine.expandedPrefix') }} {{ contributorProfile.image.creditLine }}
                </div>
            </div>

            <div class="space-y-10 md:space-y-16">
                <div v-if="contributorProfile.program_packages.length > 0">
                    <h2 class="font-semibold text-xl text-deep-teal-700">
                        {{ $t('contributorProfiles.show.contributeEvents') }}
                    </h2>

                    <ol class="font-semibold leading-none space-y-4 mt-6">
                        <li
                            v-for="programPackage in contributorProfile.program_packages"
                            :key="programPackage.slug"
                        >
                            <Component
                                :is="programPackage.event_type === 'external' || programPackage.eventFormat.language !== locale ? 'a' : Link"
                                :href="programPackage.event_type === 'external' ? programPackage.external_url : programPackage.url"
                                :target="programPackage.event_type === 'external' || programPackage.eventFormat.language !== locale ? '_blank' : '_self'"
                                :rel="programPackage.event_type === 'external' || programPackage.eventFormat.language !== locale ? 'noopener noreferrer' : ''"
                                class="flex justify-between items-center gap-x-10 py-4 px-4 md:px-6 bg-gray-50 hover:bg-deep-teal-100 focus:bg-deep-teal-100 focus:outline-none rounded-xl group"
                            >
                                <div class="flex flex-col md:flex-row md:flex-1 md:items-center gap-x-2 gap-y-2">
                                    <div class="shrink-0 inline-flex items-center md:w-32 group-hover:text-deep-teal-700 group-focus:text-deep-teal-700">
                                        <span>
                                            <CalendarDaysIcon
                                                class="shrink-0 size-5 text-gray-500 group-hover:text-deep-teal-600 mr-1.5 -mt-[3px]"
                                                aria-hidden="true"
                                            />
                                        </span>

                                        <span>
                                            {{ useGetLocalizedDateTime(programPackage.start_date, 'P') }}
                                        </span>
                                    </div>

                                    <div class="group-hover:text-deep-teal-700 group-focus:text-deep-teal-700 line-clamp-2 md:line-clamp-1">
                                        {{ programPackage.title }}
                                    </div>
                                </div>

                                <ArrowLongRightIcon
                                    class="hidden md:block size-6 group-hover:text-deep-teal-700 group-focus:text-deep-teal-700 group-hover:translate-x-2 group-focus:translate-x-2 transition-transform"
                                    aria-hidden="true"
                                />
                            </Component>
                        </li>
                    </ol>
                </div>

                <div v-if="Object.keys(contributorProfile.articles).length > 0">
                    <h2 class="font-semibold text-xl text-deep-teal-700">
                        {{ $t('contributorProfiles.show.contributeArticles') }}
                    </h2>

                    <ol class="font-semibold leading-none space-y-4 mt-6">
                        <li
                            v-for="article in contributorProfile.articles"
                            :key="article.slug"
                        >
                            <Link
                                :href="article.url"
                                class="flex justify-between items-center gap-x-10 py-4 px-4 md:px-6 bg-gray-50 hover:bg-deep-teal-100 focus:bg-deep-teal-100 focus:outline-none rounded-xl group"
                            >
                                <div class="flex md:flex-1 md:items-center gap-x-2 gap-y-2">
                                    <span
                                        class="shrink-0 size-5 text-gray-500 group-hover:text-deep-teal-600 mr-1.5 -mt-[3px]"
                                        aria-hidden="true"
                                        :title="$t(`enums.collectionType.${article.collectionType}`)"
                                        v-html="article.icon"
                                    />

                                    <div class="group-hover:text-deep-teal-700 group-focus:text-deep-teal-700 line-clamp-2 md:line-clamp-1">
                                        {{ article.title }}
                                    </div>
                                </div>

                                <ArrowLongRightIcon
                                    class="hidden md:block size-6 group-hover:text-deep-teal-700 group-focus:text-deep-teal-700 group-hover:translate-x-2 group-focus:translate-x-2 transition-transform"
                                    aria-hidden="true"
                                />
                            </Link>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    </div>
</template>
